import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for managing scroll position and sticky elements
 * @param {React.RefObject} elementRef - Ref for the element to track
 * @returns {Object} - Scroll position management data
 */
const useScrollPosition = (elementRef) => {
  const [style, setStyle] = useState({});
  const initialPositionRef = useRef(null);
  const ticking = useRef(false);
  
  // Функція для збереження початкової позиції елемента
  const saveInitialPosition = useCallback(() => {
    if (!elementRef.current || initialPositionRef.current !== null) return;
    
    // Зберігаємо початкову позицію елемента та його розміри
    const rect = elementRef.current.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset;
    
    initialPositionRef.current = {
      top: rect.top + scrollY,
      width: rect.width
    };
  }, [elementRef]);
  
  const handleScroll = useCallback(() => {
    if (!elementRef.current || !initialPositionRef.current) return;
    
    // Використовуємо requestAnimationFrame для оптимізації продуктивності
    if (!ticking.current) {
      requestAnimationFrame(() => {
        // Використовуємо розміри вікна для визначення, чи ми на десктопі
        const isDesktop = window.matchMedia('(min-width: 900px)').matches;
        
        // Перевіряємо, чи потрібно зробити елемент липким
        if (isDesktop) {
          // Отримуємо поточну позицію скролу
          const scrollY = window.scrollY || window.pageYOffset;
          
          // Отримуємо розміри елемента та вікна
          const elementRect = elementRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          
          // Перевіряємо, чи елемент повністю поміщається на екрані
          const elementFitsScreen = elementRect.height <= windowHeight - 40; // 40px для відступів
          
          // Якщо ми проскролили нижче початкової позиції елемента
          if (scrollY > initialPositionRef.current.top - 20 && elementFitsScreen) { // Прилипання тільки якщо елемент поміщається
            // Якщо елемент повністю поміщається на екрані, застосовуємо звичайну липку позицію
            setStyle({
              position: 'fixed',
              top: 20,
              width: initialPositionRef.current.width,
              zIndex: 1000
            });
          } else {
            // Якщо елемент не поміщається повністю або ми вище початкової позиції, не застосовуємо прилипання
            setStyle({});
          }
        } else {
          // На мобільних пристроях не робимо елемент липким
          setStyle({});
        }
        
        ticking.current = false;
      });
      
      ticking.current = true;
    }
  }, [elementRef]);
  
  useEffect(() => {
    // Зберігаємо початкову позицію після повного завантаження DOM
    const initTimeout = setTimeout(saveInitialPosition, 500);

    // Функція обробки resize - зберігаємо посилання для cleanup
    const handleResize = () => {
      // При зміні розміру вікна оновлюємо початкову позицію
      initialPositionRef.current = null;
      saveInitialPosition();
      handleScroll();
    };

    // Додаємо обробники подій
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(initTimeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, saveInitialPosition]);
  
  return {
    style
  };
};

export default useScrollPosition;
